import { postCouponCodeMutation, type PostCouponCodeResponseDto } from '@helios/frontend-api-v2-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useQueryClient as useLegacyQueryClient } from 'react-query';
import { trackEvent } from '@helios/frontend-core';
import { CouponConfig } from '../../hooks/useCouponConfig';
import { getFriendlyError } from '../../utils/error';
import { QUERY_KEY as USERS_QUERY_KEY } from '../user/useUser';
import { PRODUCTS_QUERY_KEY } from './useProductCategories';

export interface UsePostCouponCode {
  isLoading: boolean;
  postCouponCode: (couponId: string) => void;
  data?: PostCouponCodeResponseDto;
  error?: string;
  isSuccess?: boolean;
}

export const usePostCouponCode = (config?: CouponConfig): UsePostCouponCode => {
  const {
    id,
    pointsCost,
    discount,
    name,
    codeIssuer,
    description,
    termsAndConditions,
    partnerUrl,
    expirationDate,
    startDate,
    couponValueType,
    couponValueAmount,
  } = config || {};
  const queryClient = useQueryClient();
  const legacyQueryClient = useLegacyQueryClient();

  const trackEventProperties = {
    couponId: id,
    pointsCost,
    discount,
    name,
    codeIssuer,
    description,
    termsAndConditions,
    redemptionUrl: partnerUrl,
    expirationDate,
    startDate,
    couponValueType,
    couponValueAmount,
  };

  const { mutate, isPending, error, data, isSuccess } = useMutation({
    ...postCouponCodeMutation(),
    onSuccess: (res) => {
      legacyQueryClient.invalidateQueries([PRODUCTS_QUERY_KEY]);
      legacyQueryClient.invalidateQueries(USERS_QUERY_KEY);
      queryClient.invalidateQueries({ queryKey: [{ _id: 'getProduct' }] });
      trackEvent('coupon-redeem-success', { ...trackEventProperties, code: res.couponCode });
    },
    onError: () => {
      trackEvent('coupon-redeem-failure', trackEventProperties);
    },
  });

  return {
    postCouponCode: (couponId: string) => mutate({ path: { id: couponId } }),
    data,
    error: getFriendlyError(error as AxiosError, 'coupon code') || undefined,
    isLoading: isPending,
    isSuccess,
  };
};

export default usePostCouponCode;
